import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useLanguage } from '../../context/LanguageContext';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import LanguageSelector from './LanguageSelector';
import {
  Scan, Loader2, AlertTriangle, CheckCircle2, Clock, ChevronRight,
  Search, Plus, FileText, Send, X, Eye, ArrowRight, RefreshCw,
  AlertCircle, Inbox, Users, Globe, Link2, ChevronDown, ChevronUp, Sparkles,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const URGENCY_COLORS = {
  critical: 'bg-red-100 text-red-800 border-red-300',
  high: 'bg-orange-100 text-orange-800 border-orange-300',
  normal: 'bg-slate-100 text-slate-700 border-slate-300',
};

const TYPE_LABELS = {
  new_order: { es: 'Nuevo Pedido', en: 'New Order', icon: Plus, color: 'text-green-600' },
  incident: { es: 'Incidencia', en: 'Incident', icon: AlertTriangle, color: 'text-red-600' },
  query: { es: 'Consulta', en: 'Query', icon: Search, color: 'text-blue-600' },
  response: { es: 'Respuesta', en: 'Response', icon: CheckCircle2, color: 'text-emerald-600' },
  other: { es: 'Otro', en: 'Other', icon: FileText, color: 'text-slate-500' },
};

const STATUS_CONFIG = {
  Contactando: { color: 'bg-sky-500', label: 'Contactando' },
  Consultando: { color: 'bg-lime-600', label: 'Consultando' },
  Redactor: { color: 'bg-orange-500', label: 'Redactor' },
  Revision: { color: 'bg-slate-500', label: 'Revision' },
  Medio: { color: 'bg-fuchsia-500', label: 'Medio' },
  Publicada: { color: 'bg-green-700', label: 'Publicada' },
  Cancelada: { color: 'bg-red-500', label: 'Cancelada' },
  Incidencia: { color: 'bg-pink-500', label: 'Incidencia' },
  Programado: { color: 'bg-brand-600', label: 'Programado' },
  'Espera Proforma': { color: 'bg-yellow-500', label: 'Espera Proforma' },
};

const SupervisorTab = () => {
  const { language } = useLanguage();
  const [view, setView] = useState('dashboard'); // dashboard | inbox | orders | order-detail | new-order
  const [stats, setStats] = useState(null);
  const [scannedEmails, setScannedEmails] = useState([]);
  const [orders, setOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [showDraft, setShowDraft] = useState(null);
  const [newOrder, setNewOrder] = useState({
    email_id: '', client_name: '', client_email: '', website: '', url: '', anchor_text: '', notes: '', priority: 'normal',
  });
  const [extractingId, setExtractingId] = useState(null);
  const [urgentDraft, setUrgentDraft] = useState(null);
  const [wizard, setWizard] = useState(null);
  const [writers, setWriters] = useState([]);
  const [showWriterPicker, setShowWriterPicker] = useState(null);
  const [translatingDraft, setTranslatingDraft] = useState(false); // order_id or 'wizard'

  const t = language === 'es' ? {
    title: 'Supervisor', scan: 'Escanear Bandeja', scanning: 'Escaneando...',
    dashboard: 'Panel', inbox: 'Bandeja IA', orders: 'Pedidos', newOrder: 'Nuevo Pedido',
    activeOrders: 'Pedidos Activos', totalOrders: 'Total Pedidos', unprocessed: 'Sin Procesar',
    urgent: 'Urgentes', status: 'Estado', priority: 'Prioridad', client: 'Cliente',
    website: 'Medio', url: 'URL', anchor: 'Anchor Text', notes: 'Notas',
    create: 'Crear Pedido', save: 'Guardar', cancel: 'Cancelar', back: 'Volver',
    generateDraft: 'Generar Borrador', advanceStep: 'Avanzar Paso', history: 'Historial',
    drafts: 'Borradores', noDrafts: 'Sin borradores', noOrders: 'No hay pedidos',
    search: 'Buscar pedido (dominio, cliente...)', markProcessed: 'Marcar Procesado',
    createFromEmail: 'Crear Pedido', viewOrder: 'Ver Pedido', workflow: 'Flujo de Trabajo',
    to: 'Para', subject: 'Asunto', body: 'Cuerpo', draftPending: 'Borrador pendiente',
    // Explanations
    dashboardDesc: 'Vista general del estado de todos los pedidos y emails pendientes. Haz clic en las tarjetas para ir a cada sección.',
    inboxDesc: 'Aquí aparecen los emails nuevos clasificados por la IA. Pulsa "Escanear Bandeja" para buscar nuevos emails. La IA detecta automáticamente si es un pedido, incidencia, consulta o respuesta, y extrae los datos del cliente, medio y URL.',
    ordersDesc: 'Lista de todos los pedidos en curso. Cada pedido sigue un flujo: Recibido > Consulta Editor > Redacción > Control de Calidad > Validación Cliente > Publicación. Usa los filtros para encontrar pedidos por estado.',
    scanDesc: 'Escanea la bandeja de entrada y clasifica los emails automáticamente con IA. Detecta urgencias y extrae datos de pedidos.',
    extractDesc: 'La IA analiza el email y extrae: nombre del cliente, medio/web, URL, anchor text y un resumen.',
    createFromEmailDesc: 'Crea un pedido nuevo con los datos que la IA ha extraído del email.',
    markProcessedDesc: 'Marca el email como procesado para que no aparezca más en esta bandeja.',
    generateDraftDesc: 'La IA genera un borrador de email profesional según el paso actual del pedido. El borrador NO se envía automáticamente.',
    advanceStepDesc: 'Avanza el pedido al siguiente paso del flujo de trabajo.',
    urgentResponseDesc: 'Genera un borrador de respuesta empática y profesional para emails urgentes.',
    newOrderDesc: 'Crea un pedido manualmente introduciendo los datos del cliente, medio y URL.',
    workflowDesc: 'Historial completo de cambios de estado del pedido con fechas y responsable.',
    draftsDesc: 'Todos los borradores generados para este pedido. Los marcados con "IA" fueron escritos por inteligencia artificial.',
  } : {
    title: 'Supervisor', scan: 'Scan Inbox', scanning: 'Scanning...',
    dashboard: 'Dashboard', inbox: 'AI Inbox', orders: 'Orders', newOrder: 'New Order',
    activeOrders: 'Active Orders', totalOrders: 'Total Orders', unprocessed: 'Unprocessed',
    urgent: 'Urgent', status: 'Status', priority: 'Priority', client: 'Client',
    website: 'Website', url: 'URL', anchor: 'Anchor Text', notes: 'Notes',
    create: 'Create Order', save: 'Save', cancel: 'Cancel', back: 'Back',
    generateDraft: 'Generate Draft', advanceStep: 'Advance Step', history: 'History',
    drafts: 'Drafts', noDrafts: 'No drafts', noOrders: 'No orders',
    search: 'Search order (domain, client...)', markProcessed: 'Mark Processed',
    createFromEmail: 'Create Order', viewOrder: 'View Order', workflow: 'Workflow',
    to: 'To', subject: 'Subject', body: 'Body', draftPending: 'Draft pending',
    dashboardDesc: 'Overview of all orders and pending emails. Click on cards to navigate to each section.',
    inboxDesc: 'New emails classified by AI appear here. Click "Scan Inbox" to check for new emails. AI automatically detects if it\'s an order, incident, query or response, and extracts client, website and URL data.',
    ordersDesc: 'List of all orders in progress. Each order follows a workflow: Received > Editor Query > Writing > QA > Client Validation > Publication. Use filters to find orders by status.',
    scanDesc: 'Scans the inbox and automatically classifies emails using AI. Detects urgencies and extracts order data.',
    extractDesc: 'AI analyzes the email and extracts: client name, website, URL, anchor text and a summary.',
    createFromEmailDesc: 'Creates a new order with the data AI extracted from the email.',
    markProcessedDesc: 'Marks the email as processed so it no longer appears in this inbox.',
    generateDraftDesc: 'AI generates a professional email draft based on the current order step. The draft is NOT sent automatically.',
    advanceStepDesc: 'Advances the order to the next workflow step.',
    urgentResponseDesc: 'Generates an empathetic and professional draft response for urgent emails.',
    newOrderDesc: 'Create an order manually by entering client, website and URL data.',
    workflowDesc: 'Complete history of order status changes with dates and who made the change.',
    draftsDesc: 'All drafts generated for this order. Those marked "AI" were written by artificial intelligence.',
  };

  const loadStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/supervisor/stats`);
      setStats(res.data);
    } catch (err) { console.error(err); }
  }, []);

  const loadScanned = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/supervisor/scanned`, { params: { processed: false, limit: 50 } });
      setScannedEmails(res.data.items || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  const loadOrders = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/supervisor/orders`, {
        params: { status: filterStatus, search: searchQuery, limit: 100 }
      });
      setOrders(res.data.orders || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [filterStatus, searchQuery]);

  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => { if (view === 'inbox') loadScanned(); }, [view, loadScanned]);
  useEffect(() => { if (view === 'orders') loadOrders(); }, [view, loadOrders]);
  useEffect(() => {
    const loadWriters = async () => {
      try {
        const res = await axios.get(`${API}/admin/writers`);
        setWriters(res.data.writers || []);
      } catch (err) { console.error(err); }
    };
    loadWriters();
  }, []);

  const handleScan = async () => {
    setScanning(true);
    try {
      const res = await axios.post(`${API}/supervisor/scan`);
      toast.success(language === 'es' ? `${res.data.scanned} emails escaneados` : `${res.data.scanned} emails scanned`);
      loadScanned();
      loadStats();
    } catch (err) { toast.error('Error'); }
    finally { setScanning(false); }
  };

  const handleMarkProcessed = async (scanId) => {
    await axios.put(`${API}/supervisor/scanned/${scanId}/process`);
    loadScanned();
    loadStats();
  };

  const handleCreateOrder = async () => {
    if (!newOrder.client_name || !newOrder.client_email) {
      toast.error(language === 'es' ? 'Nombre y email del cliente requeridos' : 'Client name and email required');
      return;
    }
    try {
      const res = await axios.post(`${API}/supervisor/orders`, newOrder);
      toast.success(language === 'es' ? `Pedido ${res.data.order_id} creado` : `Order ${res.data.order_id} created`);
      setNewOrder({ email_id: '', client_name: '', client_email: '', website: '', url: '', anchor_text: '', notes: '', priority: 'normal' });
      setView('orders');
      loadOrders();
      loadStats();
    } catch (err) { toast.error('Error'); }
  };

  const handleCreateFromEmail = (email) => {
    const ext = email.extracted_data || {};
    setNewOrder({
      email_id: email.email_id,
      client_name: ext.client_name || email.from_name,
      client_email: ext.client_email || email.from_email,
      website: ext.website || '',
      url: ext.url || '',
      anchor_text: ext.anchor_text || '',
      notes: ext.summary || `${email.subject}\n${email.preview}`,
      priority: email.urgency === 'critical' ? 'critical' : email.urgency === 'high' ? 'urgent' : 'normal',
    });
    setView('new-order');
  };

  const handleExtractAI = async (scanId) => {
    setExtractingId(scanId);
    try {
      const res = await axios.post(`${API}/supervisor/ai/extract-from-email`, { scan_id: scanId });
      if (res.data.extracted) {
        toast.success(language === 'es' ? 'Datos extraídos con IA' : 'Data extracted with AI');
        loadScanned();
      }
    } catch (err) { toast.error('Error IA'); }
    finally { setExtractingId(null); }
  };

  const handleUrgentResponse = async (scanId) => {
    setExtractingId(scanId);
    try {
      const res = await axios.post(`${API}/supervisor/ai/urgent-response`, { scan_id: scanId });
      setUrgentDraft(res.data);
      toast.success(language === 'es' ? 'Borrador urgente generado' : 'Urgent draft generated');
    } catch (err) { toast.error('Error IA'); }
    finally { setExtractingId(null); }
  };

  // ==================== GUIDED WIZARD ====================

  const startWizard = async (email) => {
    setWizard({ step: 'analyzing', email, extracted: null, orderId: null, draft: null });
    try {
      const res = await axios.post(`${API}/supervisor/ai/extract-from-email`, { scan_id: email.scan_id });
      const extracted = res.data.extracted || {};
      await loadScanned();
      setWizard(prev => ({
        ...prev,
        step: 'confirm_data',
        extracted: {
          client_name: extracted.client_name || email.from_name,
          client_email: extracted.client_email || email.from_email,
          website: extracted.website || '',
          url: extracted.url || '',
          anchor_text: extracted.anchor_text || '',
          summary: extracted.summary || email.subject,
        }
      }));
    } catch {
      setWizard(prev => ({
        ...prev,
        step: 'confirm_data',
        extracted: {
          client_name: email.from_name,
          client_email: email.from_email,
          website: '', url: '', anchor_text: '',
          summary: email.subject,
        }
      }));
    }
  };

  const wizardCreateOrder = async () => {
    if (!wizard?.extracted) return;
    setWizard(prev => ({ ...prev, step: 'creating_order' }));
    try {
      const res = await axios.post(`${API}/supervisor/orders`, {
        email_id: wizard.email.email_id,
        client_name: wizard.extracted.client_name,
        client_email: wizard.extracted.client_email,
        website: wizard.extracted.website,
        url: wizard.extracted.url,
        anchor_text: wizard.extracted.anchor_text,
        notes: wizard.extracted.summary,
        priority: wizard.email.urgency === 'critical' ? 'critical' : wizard.email.urgency === 'high' ? 'urgent' : 'normal',
      });
      const orderId = res.data.order_id;
      const orderNumber = res.data.order_number;
      toast.success(language === 'es' ? `Pedido #${orderNumber} creado` : `Order #${orderNumber} created`);
      setWizard(prev => ({ ...prev, step: 'generating_draft', orderId, orderNumber }));
      try {
        const draftRes = await axios.post(`${API}/supervisor/orders/${orderId}/generate-draft`);
        setWizard(prev => ({ ...prev, step: 'review_draft', draft: draftRes.data.draft }));
      } catch {
        setWizard(prev => ({ ...prev, step: 'draft_error' }));
      }
      loadStats();
      loadOrders();
    } catch {
      toast.error('Error');
      setWizard(prev => ({ ...prev, step: 'confirm_data' }));
    }
  };

  const wizardFinish = async () => {
    if (wizard?.email) {
      await handleMarkProcessed(wizard.email.scan_id);
    }
    setWizard(null);
    loadScanned();
    loadStats();
  };

  const wizardAdvanceStatus = async (skipSend = false) => {
    if (!wizard?.orderId) return;
    try {
      const draft = wizard.draft || {};
      const res = await axios.post(`${API}/supervisor/orders/${wizard.orderId}/advance-and-send`, {
        draft_to: draft.to || '',
        draft_subject: draft.subject || '',
        draft_body: draft.body || '',
        skip_send: skipSend,
      });
      setWizard(prev => ({ ...prev, currentStatus: res.data.status }));
      if (res.data.email_sent) {
        toast.success(language === 'es' ? `Email enviado a ${draft.to} | Estado: ${res.data.status}` : `Email sent to ${draft.to} | Status: ${res.data.status}`);
      } else if (skipSend) {
        toast.success(language === 'es' ? `Avanzado a: ${res.data.status} (sin enviar email)` : `Advanced to: ${res.data.status} (email skipped)`);
      } else if (res.data.email_error) {
        toast.error(language === 'es' ? `Avanzado pero error al enviar: ${res.data.email_error}` : `Advanced but email error: ${res.data.email_error}`);
      } else {
        toast.success(language === 'es' ? `Avanzado a: ${res.data.status}` : `Advanced to: ${res.data.status}`);
      }
      loadStats();
    } catch { toast.error('Error'); }
  };

  const wizardGoBackStatus = async () => {
    if (!wizard?.orderId) return;
    const doChange = confirm(language === 'es'
      ? '¿Quieres cambiar al estado anterior? Si solo estás consultando, pulsa Cancelar.'
      : 'Do you want to change to the previous status? If you\'re just checking, click Cancel.');
    if (!doChange) return;
    const reason = prompt(language === 'es' ? 'Motivo del cambio (opcional):' : 'Reason for change (optional):') || '';
    try {
      const res = await axios.put(`${API}/supervisor/orders/${wizard.orderId}/status-back`, { reason });
      setWizard(prev => ({ ...prev, currentStatus: res.data.status }));
      toast.success(language === 'es' ? `Cambiado a: ${res.data.status}` : `Changed to: ${res.data.status}`);
      loadStats();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  const wizardChangeStatus = async (targetStatus, orderId = null) => {
    const oid = orderId || wizard?.orderId;
    if (!oid) return;

    // Check if already in this status
    const currentStatus = orderId ? selectedOrder?.status : (wizard?.currentStatus || 'Contactando');
    if (targetStatus === currentStatus) return;

    // If changing to Redactor, show writer picker first
    if (targetStatus === 'Redactor') {
      setShowWriterPicker({ orderId: oid, targetStatus, context: orderId ? 'detail' : 'wizard' });
      return;
    }

    const reason = prompt(language === 'es' ? `Motivo del cambio a "${targetStatus}":` : `Reason for changing to "${targetStatus}":`) || '';
    if (reason === null) return; // User cancelled
    try {
      const res = await axios.put(`${API}/supervisor/orders/${oid}/status-back`, { target_status: targetStatus, reason });
      if (wizard && !orderId) {
        setWizard(prev => ({ ...prev, currentStatus: res.data.status }));
        await wizardGenerateNewDraft();
      } else {
        const orderRes = await axios.get(`${API}/supervisor/orders/${oid}`);
        setSelectedOrder(orderRes.data);
      }
      toast.success(language === 'es' ? `Estado: ${targetStatus}` : `Status: ${targetStatus}`);
      loadStats();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  const assignWriterAndChangeStatus = async (writerId, writerName, writerEmail) => {
    if (!showWriterPicker) return;
    const { orderId, targetStatus, context } = showWriterPicker;
    const reason = `Asignado a redactor: ${writerName} (${writerEmail})`;
    try {
      // Change status to Redactor
      await axios.put(`${API}/supervisor/orders/${orderId}/status-back`, { target_status: targetStatus, reason });
      // Update the order with the writer info
      await axios.put(`${API}/admin/orders/${orderId}`, {
        writer: writerName,
        writer_id: writerId,
      });
      if (context === 'wizard' && wizard) {
        setWizard(prev => ({ ...prev, currentStatus: targetStatus }));
        await wizardGenerateNewDraft();
      } else {
        const orderRes = await axios.get(`${API}/supervisor/orders/${orderId}`);
        setSelectedOrder(orderRes.data);
      }
      toast.success(language === 'es' ? `Asignado a ${writerName} | Estado: Redactor` : `Assigned to ${writerName} | Status: Redactor`);
      loadStats();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
    finally { setShowWriterPicker(null); }
  };

  const wizardGenerateNewDraft = async () => {
    if (!wizard?.orderId) return;
    try {
      const res = await axios.post(`${API}/supervisor/orders/${wizard.orderId}/generate-draft`);
      setWizard(prev => ({ ...prev, draft: res.data.draft }));
      toast.success(language === 'es' ? 'Nuevo borrador generado' : 'New draft generated');
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  const translateDraft = async (targetLang) => {
    if (!wizard?.draft?.body) return;
    setTranslatingDraft(true);
    try {
      const res = await axios.post(`${API}/email-tool/translate`, {
        text: wizard.draft.body,
        target_language: targetLang,
      });
      const translatedSubject = await axios.post(`${API}/email-tool/translate`, {
        text: wizard.draft.subject,
        target_language: targetLang,
      });
      setWizard(prev => ({
        ...prev,
        draft: {
          ...prev.draft,
          body: res.data.translated,
          subject: translatedSubject.data.translated,
        }
      }));
      toast.success(language === 'es' ? 'Borrador traducido' : 'Draft translated');
    } catch { toast.error('Error IA'); }
    finally { setTranslatingDraft(false); }
  };

  const handleAdvanceStatus = async (orderId, currentStatus) => {
    const statusOrder = ['Contactando', 'Consultando', 'Redactor', 'Revision', 'Medio', 'Publicada'];
    const idx = statusOrder.indexOf(currentStatus);
    if (idx < 0 || idx >= statusOrder.length - 1) return;
    // First generate draft, then send and advance
    try {
      const draftRes = await axios.post(`${API}/supervisor/orders/${orderId}/generate-draft`);
      const draft = draftRes.data.draft;
      const res = await axios.post(`${API}/supervisor/orders/${orderId}/advance-and-send`, {
        draft_to: draft.to || '',
        draft_subject: draft.subject || '',
        draft_body: draft.body || '',
        skip_send: false,
      });
      if (res.data.email_sent) {
        toast.success(language === 'es' ? `Email enviado | Estado: ${res.data.status}` : `Email sent | Status: ${res.data.status}`);
      } else {
        toast.success(`${res.data.status}`);
      }
      const orderRes = await axios.get(`${API}/supervisor/orders/${orderId}`);
      setSelectedOrder(orderRes.data);
      loadStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    }
  };

  const handleGenerateDraft = async (orderId) => {
    try {
      const res = await axios.post(`${API}/supervisor/orders/${orderId}/generate-draft`);
      setShowDraft(res.data.draft);
      toast.success(language === 'es' ? 'Borrador generado' : 'Draft generated');
      // Reload order
      const orderRes = await axios.get(`${API}/supervisor/orders/${orderId}`);
      setSelectedOrder(orderRes.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    }
  };

  const openOrder = async (orderId) => {
    try {
      const res = await axios.get(`${API}/supervisor/orders/${orderId}`);
      setSelectedOrder(res.data);
      setView('order-detail');
    } catch (err) { toast.error('Error'); }
  };

  return (
    <div className="space-y-4" data-testid="supervisor-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h2 className="text-2xl font-bold">{t.title}</h2>
          <div className="flex gap-1">
            {['dashboard', 'inbox', 'orders'].map(v => (
              <Button key={v} variant={view === v ? 'default' : 'ghost'} size="sm"
                className={view === v ? 'bg-indigo-600 hover:bg-indigo-700 text-white' : ''}
                onClick={() => setView(v)}>
                {v === 'dashboard' ? t.dashboard : v === 'inbox' ? t.inbox : t.orders}
              </Button>
            ))}
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => { setNewOrder({ email_id: '', client_name: '', client_email: '', website: '', url: '', anchor_text: '', notes: '', priority: 'normal' }); setView('new-order'); }}>
            <Plus className="h-4 w-4 mr-1" />{t.newOrder}
          </Button>
          <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700" onClick={handleScan} disabled={scanning} title={t.scanDesc}>
            {scanning ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Scan className="h-4 w-4 mr-1" />}
            {scanning ? t.scanning : t.scan}
          </Button>
        </div>
      </div>

      {/* === DASHBOARD === */}
      {view === 'dashboard' && stats && (
        <div className="space-y-4">
          <p className="text-sm text-slate-500">{t.dashboardDesc}</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="cursor-pointer hover:shadow-md" onClick={() => setView('orders')}>
              <CardContent className="p-4 text-center">
                <p className="text-3xl font-bold text-indigo-600">{stats.active_orders}</p>
                <p className="text-sm text-muted-foreground">{t.activeOrders}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <p className="text-3xl font-bold text-slate-600">{stats.total_orders}</p>
                <p className="text-sm text-muted-foreground">{t.totalOrders}</p>
              </CardContent>
            </Card>
            <Card className="cursor-pointer hover:shadow-md" onClick={() => setView('inbox')}>
              <CardContent className="p-4 text-center">
                <p className="text-3xl font-bold text-amber-600">{stats.unprocessed_emails}</p>
                <p className="text-sm text-muted-foreground">{t.unprocessed}</p>
              </CardContent>
            </Card>
            <Card className={stats.urgent_emails > 0 ? 'border-red-300 bg-red-50' : ''}>
              <CardContent className="p-4 text-center">
                <p className={`text-3xl font-bold ${stats.urgent_emails > 0 ? 'text-red-600' : 'text-slate-400'}`}>{stats.urgent_emails}</p>
                <p className="text-sm text-muted-foreground">{t.urgent}</p>
              </CardContent>
            </Card>
          </div>

          {/* Status pipeline */}
          {stats.by_status && Object.keys(stats.by_status).length > 0 && (
            <Card>
              <CardHeader><CardTitle className="text-sm">{t.workflow}</CardTitle></CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(stats.by_status).map(([status, count]) => (
                    <button key={status}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm text-white ${STATUS_CONFIG[status]?.color || 'bg-slate-500'}`}
                      onClick={() => { setFilterStatus(status); setView('orders'); }}>
                      <span className="font-medium">{count}</span>
                      <span>{STATUS_CONFIG[status]?.label || status}</span>
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Quick search */}
          <Card>
            <CardContent className="p-4">
              <div className="relative">
                <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input className="pl-9" placeholder={t.search} value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { setView('orders'); loadOrders(); }}} />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* === AI INBOX (scanned emails) === */}
      {view === 'inbox' && (
        <div className="space-y-3">
          <p className="text-sm text-slate-500">{t.inboxDesc}</p>
          {loading ? (
            <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
          ) : scannedEmails.length === 0 ? (
            <Card><CardContent className="p-8 text-center text-sm text-slate-400">
              {language === 'es' ? 'No hay emails sin procesar. Pulsa "Escanear Bandeja" para buscar nuevos.' : 'No unprocessed emails. Click "Scan Inbox" to check.'}
            </CardContent></Card>
          ) : (
            scannedEmails.map(em => {
              const typeInfo = TYPE_LABELS[em.type] || TYPE_LABELS.other;
              const TypeIcon = typeInfo.icon;
              return (
                <Card key={em.scan_id} className={`${em.urgency === 'critical' ? 'border-red-400 bg-red-50/50' : em.urgency === 'high' ? 'border-orange-300 bg-orange-50/30' : ''}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <TypeIcon className={`h-5 w-5 mt-0.5 shrink-0 ${typeInfo.color}`} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-sm">{em.from_name}</span>
                          <span className="text-xs text-slate-500">&lt;{em.from_email}&gt;</span>
                          <Badge className={`text-xs ${URGENCY_COLORS[em.urgency]}`}>{em.urgency}</Badge>
                          <Badge variant="outline" className="text-xs">{typeInfo[language] || typeInfo.es}</Badge>
                          {em.ai_classified && <Badge className="text-xs bg-purple-100 text-purple-700 border-purple-300">IA</Badge>}
                        </div>
                        <p className="text-sm font-medium mt-1">{em.subject}</p>
                        <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{em.preview}</p>
                        {/* Related order indicator */}
                        {em.related_order && (
                          <div className="mt-2 p-2 bg-green-50 rounded border border-green-200 text-xs">
                            <p className="font-medium text-green-800">
                              {language === 'es' ? 'Pedido relacionado' : 'Related order'}: #{em.related_order.order_number} — {em.related_order.product_url}
                            </p>
                            <p className="text-green-600">{language === 'es' ? 'Estado' : 'Status'}: {em.related_order.status}</p>
                          </div>
                        )}
                        {/* Extracted data from AI */}
                        {em.extracted_data && em.extracted_data.summary && !em.related_order && (
                          <div className="mt-2 p-2 bg-purple-50 rounded border border-purple-200 text-xs space-y-0.5">
                            <p className="font-medium text-purple-800">{em.extracted_data.summary}</p>
                            {em.extracted_data.website && <p><span className="text-purple-600">Medio:</span> {em.extracted_data.website}</p>}
                            {em.extracted_data.url && <p><span className="text-purple-600">URL:</span> {em.extracted_data.url}</p>}
                            {em.extracted_data.anchor_text && <p><span className="text-purple-600">Anchor:</span> {em.extracted_data.anchor_text}</p>}
                          </div>
                        )}
                        <p className="text-xs text-slate-400 mt-1">{em.date ? new Date(em.date).toLocaleString() : ''}</p>
                      </div>
                      <div className="flex flex-col gap-1 shrink-0">
                        {em.related_order ? (
                          <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white text-xs"
                            onClick={() => { openOrder(em.related_order.id); handleMarkProcessed(em.scan_id); }}
                            title={language === 'es' ? 'Abrir el pedido relacionado' : 'Open related order'}>
                            <Eye className="h-3 w-3 mr-1" />{language === 'es' ? 'Gestionar Pedido' : 'Manage Order'}
                          </Button>
                        ) : (
                          <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700 text-white text-xs" onClick={() => startWizard(em)}
                            disabled={extractingId === em.scan_id}
                            title={language === 'es' ? 'El agente analiza el email y te guía paso a paso' : 'The agent analyzes the email and guides you step by step'}>
                            {extractingId === em.scan_id ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Sparkles className="h-3 w-3 mr-1" />}
                            {language === 'es' ? 'Procesar' : 'Process'}
                          </Button>
                        )}
                        {(em.urgency === 'critical' || em.urgency === 'high') && (
                          <Button size="sm" variant="outline" className="text-xs border-red-300 text-red-600 hover:bg-red-50"
                            onClick={() => handleUrgentResponse(em.scan_id)} disabled={extractingId === em.scan_id} title={t.urgentResponseDesc}>
                            <AlertTriangle className="h-3 w-3 mr-1" />
                            {language === 'es' ? 'Urgente' : 'Urgent'}
                          </Button>
                        )}
                        <Button size="sm" variant="ghost" className="text-xs" onClick={() => handleMarkProcessed(em.scan_id)} title={t.markProcessedDesc}>
                          <CheckCircle2 className="h-3 w-3 mr-1" />{language === 'es' ? 'Ignorar' : 'Skip'}
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>
      )}

      {/* Urgent draft modal */}
      {urgentDraft && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50" onClick={() => setUrgentDraft(null)}>
          <Card className="w-full max-w-lg mx-4 border-red-300" onClick={(e) => e.stopPropagation()}>
            <CardContent className="p-5 space-y-3">
              <div className="flex items-center justify-between">
                <h4 className="font-bold text-red-700 flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4" />
                  {language === 'es' ? 'Borrador Respuesta Urgente' : 'Urgent Response Draft'}
                  {urgentDraft.ai_generated && <Badge className="text-xs bg-purple-100 text-purple-700">IA</Badge>}
                </h4>
                <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => setUrgentDraft(null)}><X className="h-4 w-4" /></Button>
              </div>
              <div className="text-sm space-y-1">
                <p><strong>{t.to}:</strong> {urgentDraft.to}</p>
                <p><strong>{t.subject}:</strong> {urgentDraft.subject}</p>
              </div>
              <div className="bg-white border rounded p-3 whitespace-pre-wrap text-sm">{urgentDraft.body}</div>
              <p className="text-xs text-slate-400">{language === 'es' ? 'Este es solo un borrador. No se ha enviado.' : 'This is just a draft. It has not been sent.'}</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* === ORDERS LIST === */}
      {view === 'orders' && (
        <div className="space-y-3">
          <p className="text-sm text-slate-500">{t.ordersDesc}</p>
          <div className="flex gap-2 items-center">
            <div className="relative flex-1">
              <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input className="pl-9 h-9" placeholder={t.search} value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && loadOrders()} />
            </div>
            <select className="border rounded px-2 py-1.5 text-sm bg-white h-9" value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}>
              <option value="">{language === 'es' ? 'Todos los estados' : 'All statuses'}</option>
              {Object.entries(STATUS_CONFIG).map(([k, v]) => (
                <option key={k} value={k}>{v.label}</option>
              ))}
            </select>
            <Button variant="ghost" size="sm" onClick={loadOrders}><RefreshCw className="h-4 w-4" /></Button>
          </div>

          {loading ? (
            <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin" /></div>
          ) : orders.length === 0 ? (
            <Card><CardContent className="p-8 text-center text-sm text-slate-400">{t.noOrders}</CardContent></Card>
          ) : (
            orders.map(order => (
              <Card key={order.id} className="cursor-pointer hover:shadow-md transition-shadow"
                onClick={() => openOrder(order.id)}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className={`w-2 h-10 rounded-full ${STATUS_CONFIG[order.status]?.color || 'bg-slate-300'}`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-sm">{order.customer_name}</span>
                        <Globe className="h-3.5 w-3.5 text-slate-400" />
                        <span className="text-sm text-slate-600">{order.product_url || '-'}</span>
                      </div>
                      <div className="flex items-center gap-2 mt-0.5">
                        <Badge className={`text-xs ${STATUS_CONFIG[order.status]?.color || 'bg-slate-500'} text-white`}>
                          {STATUS_CONFIG[order.status]?.label || order.status}
                        </Badge>
                        <Badge className={`text-xs ${order.managed_by === 'Supervisor' ? 'bg-indigo-100 text-indigo-600' : 'bg-slate-100 text-slate-600'}`}>
                          {order.managed_by || 'Humano'}
                        </Badge>
                        {order.url_1 && <span className="text-xs text-slate-400 truncate">{order.url_1}</span>}
                      </div>
                    </div>
                    <div className="text-xs text-slate-400 text-right">
                      <p>#{order.order_number}</p>
                      <p>{order.updated_at ? new Date(order.updated_at).toLocaleDateString() : ''}</p>
                    </div>
                    <ChevronRight className="h-4 w-4 text-slate-300" />
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      )}

      {/* === ORDER DETAIL === */}
      {view === 'order-detail' && selectedOrder && (
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => { setView('orders'); setSelectedOrder(null); }}>
              <ChevronDown className="h-4 w-4 mr-1 -rotate-90" />{t.back}
            </Button>
            <h3 className="font-bold flex-1">#{selectedOrder.order_number || selectedOrder.id?.slice(0,8)} — {selectedOrder.customer_name}</h3>
            <Badge className={`${STATUS_CONFIG[selectedOrder.status]?.color || 'bg-slate-500'} text-white`}>
              {STATUS_CONFIG[selectedOrder.status]?.label || selectedOrder.status}
            </Badge>
            {/* Managed by badge */}
            <Badge
              className={`text-xs cursor-pointer ${selectedOrder.managed_by === 'Supervisor' ? 'bg-indigo-100 text-indigo-700 border-indigo-300' : 'bg-slate-100 text-slate-700 border-slate-300'}`}
              onClick={async () => {
                const next = selectedOrder.managed_by === 'Supervisor' ? 'Humano' : 'Supervisor';
                await axios.put(`${API}/supervisor/orders/${selectedOrder.id}/managed-by`, { managed_by: next });
                const res = await axios.get(`${API}/supervisor/orders/${selectedOrder.id}`);
                setSelectedOrder(res.data);
                toast.success(language === 'es' ? `Gestionado por: ${next}` : `Managed by: ${next}`);
              }}
              title={language === 'es' ? 'Haz clic para cambiar quién gestiona' : 'Click to change who manages'}
            >
              {selectedOrder.managed_by === 'Supervisor' ? 'Supervisor' : 'Humano'}
            </Badge>
          </div>

          {/* Order info */}
          <Card>
            <CardContent className="p-4 grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
              <div><span className="text-slate-500">{t.client}:</span> <strong>{selectedOrder.customer_name}</strong></div>
              <div><span className="text-slate-500">Email:</span> {selectedOrder.customer_email}</div>
              <div><span className="text-slate-500">{t.website}:</span> {selectedOrder.product_url || '-'}</div>
              <div><span className="text-slate-500">{t.url}:</span> <span className="text-blue-600 break-all">{selectedOrder.url_1 || '-'}</span></div>
              <div><span className="text-slate-500">{t.anchor}:</span> <strong>{selectedOrder.anchor_text_1 || '-'}</strong></div>
              <div><span className="text-slate-500">{t.priority}:</span> <Badge className={`text-xs ${selectedOrder.supervisor_priority === 'critical' ? 'bg-red-500 text-white' : selectedOrder.supervisor_priority === 'urgent' ? 'bg-orange-500 text-white' : 'bg-slate-200'}`}>{selectedOrder.supervisor_priority || 'normal'}</Badge></div>
              {selectedOrder.information && <div className="col-span-full"><span className="text-slate-500">{t.notes}:</span> {selectedOrder.information}</div>}
            </CardContent>
          </Card>

          {/* Actions */}
          <div className="flex gap-2 flex-wrap">
            {selectedOrder.status !== 'Publicada' && selectedOrder.status !== 'Cancelada' && (
              <>
                {/* Advance - sends email */}
                <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700"
                  onClick={() => handleAdvanceStatus(selectedOrder.id, selectedOrder.status)} title={language === 'es' ? 'Genera borrador, lo envía y avanza al siguiente paso' : 'Generate draft, send it and advance'}>
                  <Send className="h-4 w-4 mr-1" />{language === 'es' ? 'Enviar y Avanzar' : 'Send & Advance'}
                </Button>
                {/* Generate draft only */}
                <Button size="sm" variant="outline" onClick={() => handleGenerateDraft(selectedOrder.id)} title={t.generateDraftDesc}>
                  <FileText className="h-4 w-4 mr-1" />{t.generateDraft}
                </Button>
                {/* Change to any status */}
                <div className="relative group">
                  <Button size="sm" variant="outline" className="text-xs">
                    {language === 'es' ? 'Cambiar Estado' : 'Change Status'} <ChevronDown className="h-3 w-3 ml-1" />
                  </Button>
                  <div className="absolute left-0 top-full mt-1 bg-white border rounded-md shadow-lg hidden group-hover:block z-20 min-w-[160px] py-1">
                    {['Contactando', 'Consultando', 'Redactor', 'Revision', 'Medio', 'Publicada', 'Incidencia', 'Cancelada'].map(s => (
                      <button key={s} className={`block w-full text-left px-3 py-1.5 text-sm ${s === selectedOrder.status ? 'font-bold bg-indigo-50 text-indigo-400 cursor-not-allowed' : 'hover:bg-slate-100 cursor-pointer'}`}
                        onClick={() => { if (s !== selectedOrder.status) wizardChangeStatus(s, selectedOrder.id); }}>
                        <span className={`inline-block w-2 h-2 rounded-full mr-2 ${STATUS_CONFIG[s]?.color || 'bg-slate-300'}`}></span>
                        {STATUS_CONFIG[s]?.label || s}
                      </button>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Draft preview */}
          {showDraft && (
            <Card className="border-amber-300 bg-amber-50">
              <CardContent className="p-4 space-y-2">
                <div className="flex items-center justify-between">
                  <h4 className="font-medium text-sm text-amber-800">{t.draftPending}</h4>
                  <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => setShowDraft(null)}><X className="h-3.5 w-3.5" /></Button>
                </div>
                <div className="text-sm space-y-1">
                  <p><strong>{t.to}:</strong> {showDraft.to || '(rellenar)'}</p>
                  <p><strong>{t.subject}:</strong> {showDraft.subject}</p>
                  <div className="bg-white rounded p-3 border mt-2 whitespace-pre-wrap text-sm">{showDraft.body}</div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Workflow history */}
          <Card>
            <CardHeader><CardTitle className="text-sm">{t.history}</CardTitle></CardHeader>
            <CardContent className="p-4">
              <p className="text-xs text-slate-400 mb-3">{t.workflowDesc}</p>
              <div className="space-y-3">
                {(selectedOrder.supervisor_history || []).map((step, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className={`w-3 h-3 rounded-full mt-1 shrink-0 ${STATUS_CONFIG[step.status]?.color || 'bg-slate-300'}`} />
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium">{step.action || step.status}</span>
                        <span className="text-xs text-slate-400">{new Date(step.timestamp).toLocaleString()}</span>
                      </div>
                      {step.notes && <p className="text-xs text-slate-500">{step.notes}</p>}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Drafts history */}
          {selectedOrder.supervisor_drafts?.length > 0 && (
            <Card>
              <CardHeader><CardTitle className="text-sm">{t.drafts} ({selectedOrder.supervisor_drafts.length})</CardTitle></CardHeader>
              <CardContent className="p-4 space-y-3">
                <p className="text-xs text-slate-400">{t.draftsDesc}</p>
                {selectedOrder.supervisor_drafts.map((draft, i) => (
                  <div key={i} className="border rounded p-3 bg-slate-50 text-sm">
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-1.5">
                        <Badge variant="outline" className="text-xs">{draft.type}</Badge>
                        {draft.ai_generated && <Badge className="text-xs bg-purple-100 text-purple-700 border-purple-300">IA</Badge>}
                      </div>
                      <span className="text-xs text-slate-400">{new Date(draft.generated_at).toLocaleString()}</span>
                    </div>
                    <p className="text-xs"><strong>{t.to}:</strong> {draft.to || '(rellenar)'}</p>
                    <p className="text-xs"><strong>{t.subject}:</strong> {draft.subject}</p>
                    <p className="text-xs text-slate-500 mt-1 line-clamp-3">{draft.body}</p>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* === NEW ORDER FORM === */}
      {view === 'new-order' && (
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => setView('dashboard')}>
              <ChevronDown className="h-4 w-4 mr-1 -rotate-90" />{t.back}
            </Button>
            <h3 className="font-bold">{t.newOrder}</h3>
          </div>
          <p className="text-sm text-slate-500">{t.newOrderDesc}</p>
          <Card>
            <CardContent className="p-4 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-slate-500">{t.client} *</label>
                  <Input value={newOrder.client_name} onChange={(e) => setNewOrder(p => ({ ...p, client_name: e.target.value }))} />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-500">Email *</label>
                  <Input type="email" value={newOrder.client_email} onChange={(e) => setNewOrder(p => ({ ...p, client_email: e.target.value }))} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-slate-500">{t.website}</label>
                  <Input value={newOrder.website} onChange={(e) => setNewOrder(p => ({ ...p, website: e.target.value }))} placeholder="elpais.com" />
                </div>
                <div>
                  <label className="text-xs font-medium text-slate-500">{t.priority}</label>
                  <select className="w-full border rounded px-3 py-2 text-sm" value={newOrder.priority}
                    onChange={(e) => setNewOrder(p => ({ ...p, priority: e.target.value }))}>
                    <option value="normal">Normal</option>
                    <option value="urgent">{language === 'es' ? 'Urgente' : 'Urgent'}</option>
                    <option value="critical">{language === 'es' ? 'Crítico' : 'Critical'}</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-500">{t.url}</label>
                <Input value={newOrder.url} onChange={(e) => setNewOrder(p => ({ ...p, url: e.target.value }))} placeholder="https://..." />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-500">{t.anchor}</label>
                <Input value={newOrder.anchor_text} onChange={(e) => setNewOrder(p => ({ ...p, anchor_text: e.target.value }))} />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-500">{t.notes}</label>
                <Textarea value={newOrder.notes} onChange={(e) => setNewOrder(p => ({ ...p, notes: e.target.value }))} rows={3} />
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="ghost" onClick={() => setView('dashboard')}>{t.cancel}</Button>
                <Button className="bg-indigo-600 hover:bg-indigo-700" onClick={handleCreateOrder}>
                  <Plus className="h-4 w-4 mr-1" />{t.create}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* ==================== WRITER PICKER MODAL ==================== */}
      {showWriterPicker && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60]" onClick={() => setShowWriterPicker(null)}>
          <Card className="w-full max-w-md mx-4 border-orange-300 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <CardContent className="p-5 space-y-4">
              <h3 className="font-bold text-lg">{language === 'es' ? 'Selecciona un redactor' : 'Select a writer'}</h3>
              <p className="text-sm text-slate-500">{language === 'es' ? 'El pedido pasará a estado "Redactor" y se asignará al redactor seleccionado.' : 'The order will change to "Writer" status and be assigned to the selected writer.'}</p>
              <div className="space-y-2">
                {/* Option: Editor writes the content */}
                <button
                  className="w-full flex items-center gap-3 p-3 border rounded-md hover:bg-teal-50 hover:border-teal-300 transition-colors text-left border-teal-200 bg-teal-50/30"
                  onClick={() => assignWriterAndChangeStatus('editor', 'Redacta el Medio', '')}>
                  <div className="w-9 h-9 rounded-full bg-teal-600 text-white flex items-center justify-center font-bold text-sm">M</div>
                  <div>
                    <p className="font-medium text-sm">{language === 'es' ? 'Redacta el Medio' : 'Editor Writes'}</p>
                    <p className="text-xs text-slate-500">{language === 'es' ? 'El editor/medio se encarga de la redacción' : 'The editor/media handles the writing'}</p>
                  </div>
                </button>
                {/* Separator */}
                <div className="border-t my-2"></div>
                {/* Writers list */}
                {writers.length === 0 ? (
                  <p className="text-sm text-slate-400">{language === 'es' ? 'No hay redactores registrados' : 'No writers registered'}</p>
                ) : (
                  writers.map(w => (
                    <button key={w.id}
                      className="w-full flex items-center gap-3 p-3 border rounded-md hover:bg-orange-50 hover:border-orange-300 transition-colors text-left"
                      onClick={() => assignWriterAndChangeStatus(w.id, w.name, w.email)}>
                      <div className="w-9 h-9 rounded-full bg-orange-500 text-white flex items-center justify-center font-bold text-sm">{(w.name || '?')[0].toUpperCase()}</div>
                      <div>
                        <p className="font-medium text-sm">{w.name}</p>
                        <p className="text-xs text-slate-500">{w.email}</p>
                      </div>
                    </button>
                  ))
                )}
              </div>
              <div className="flex justify-end">
                <Button variant="ghost" onClick={() => setShowWriterPicker(null)}>{t.cancel}</Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* ==================== GUIDED WIZARD MODAL ==================== */}
      {wizard && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setWizard(null)}>
          <Card className="w-full max-w-xl mx-4 border-indigo-300 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <CardContent className="p-6">
              {/* Step: Analyzing */}
              {wizard.step === 'analyzing' && (
                <div className="text-center py-8 space-y-3">
                  <Loader2 className="h-8 w-8 animate-spin text-indigo-500 mx-auto" />
                  <p className="font-medium">{language === 'es' ? 'Analizando email con IA...' : 'Analyzing email with AI...'}</p>
                  <p className="text-sm text-slate-500">{language === 'es' ? 'Extrayendo datos del cliente, medio, URL y anchor text' : 'Extracting client data, website, URL and anchor text'}</p>
                </div>
              )}

              {/* Step: Confirm extracted data */}
              {wizard.step === 'confirm_data' && wizard.extracted && (
                <div className="space-y-4">
                  <div className="flex items-center gap-2">
                    <Sparkles className="h-5 w-5 text-indigo-500" />
                    <h3 className="font-bold text-lg">{language === 'es' ? 'He extraído estos datos del email' : 'I extracted this data from the email'}</h3>
                  </div>
                  <p className="text-sm text-slate-500">{language === 'es' ? 'Revisa y corrige si es necesario. Cuando estés conforme, creo el pedido en tu panel.' : 'Review and correct if needed. When you\'re satisfied, I\'ll create the order in your panel.'}</p>

                  <div className="space-y-2">
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="text-xs font-medium text-slate-500">{t.client}</label>
                        <Input value={wizard.extracted.client_name} onChange={(e) => setWizard(prev => ({ ...prev, extracted: { ...prev.extracted, client_name: e.target.value } }))} />
                      </div>
                      <div>
                        <label className="text-xs font-medium text-slate-500">Email</label>
                        <Input value={wizard.extracted.client_email} onChange={(e) => setWizard(prev => ({ ...prev, extracted: { ...prev.extracted, client_email: e.target.value } }))} />
                      </div>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-slate-500">{t.website}</label>
                      <Input value={wizard.extracted.website} onChange={(e) => setWizard(prev => ({ ...prev, extracted: { ...prev.extracted, website: e.target.value } }))} placeholder="elpais.com" />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-slate-500">{t.url}</label>
                      <Input value={wizard.extracted.url} onChange={(e) => setWizard(prev => ({ ...prev, extracted: { ...prev.extracted, url: e.target.value } }))} placeholder="https://..." />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-slate-500">{t.anchor}</label>
                      <Input value={wizard.extracted.anchor_text} onChange={(e) => setWizard(prev => ({ ...prev, extracted: { ...prev.extracted, anchor_text: e.target.value } }))} />
                    </div>
                  </div>

                  <div className="flex justify-between pt-2">
                    <Button variant="ghost" onClick={() => setWizard(null)}>{t.cancel}</Button>
                    <Button className="bg-indigo-600 hover:bg-indigo-700" onClick={wizardCreateOrder}>
                      <Plus className="h-4 w-4 mr-1" />{language === 'es' ? 'Crear Pedido' : 'Create Order'}
                    </Button>
                  </div>
                </div>
              )}

              {/* Step: Creating order */}
              {wizard.step === 'creating_order' && (
                <div className="text-center py-8 space-y-3">
                  <Loader2 className="h-8 w-8 animate-spin text-indigo-500 mx-auto" />
                  <p className="font-medium">{language === 'es' ? 'Creando pedido...' : 'Creating order...'}</p>
                </div>
              )}

              {/* Step: Generating draft */}
              {wizard.step === 'generating_draft' && (
                <div className="text-center py-8 space-y-3">
                  <Loader2 className="h-8 w-8 animate-spin text-indigo-500 mx-auto" />
                  <p className="font-medium">{language === 'es' ? 'Pedido creado. Generando borrador de confirmación...' : 'Order created. Generating confirmation draft...'}</p>
                </div>
              )}

              {/* Step: Review draft */}
              {wizard.step === 'review_draft' && wizard.draft && (
                <div className="space-y-4">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="h-5 w-5 text-green-500" />
                    <h3 className="font-bold text-lg">{language === 'es' ? `Pedido #${wizard.orderNumber || wizard.orderId?.slice(0,8)} creado` : `Order #${wizard.orderNumber || wizard.orderId?.slice(0,8)} created`}</h3>
                    <Badge className={`${STATUS_CONFIG[wizard.currentStatus || 'Contactando']?.color || 'bg-blue-500'} text-white text-xs`}>
                      {wizard.currentStatus || 'Contactando'}
                    </Badge>
                  </div>
                  <p className="text-sm text-slate-500">
                    {language === 'es'
                      ? 'He preparado este borrador. NO se enviará automáticamente. Puedes avanzar, retroceder u omitir pasos.'
                      : 'I prepared this draft. It will NOT be sent automatically. You can advance, go back or skip steps.'}
                  </p>

                  <Card className="bg-amber-50 border-amber-300">
                    <CardContent className="p-4 space-y-2 text-sm">
                      <p><strong>{t.to}:</strong> {wizard.draft.to || wizard.extracted?.client_email || '(rellenar)'}</p>
                      <p><strong>{t.subject}:</strong> {wizard.draft.subject}</p>
                      <div className="bg-white rounded p-3 border mt-2 whitespace-pre-wrap text-sm max-h-40 overflow-y-auto">{wizard.draft.body}</div>
                      {wizard.draft.ai_generated && <Badge className="text-xs bg-purple-100 text-purple-700">Generado por IA</Badge>}
                    </CardContent>
                  </Card>

                  {/* Workflow navigation */}
                  <div className="border-t pt-3 space-y-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Button size="sm" variant="outline" onClick={wizardGoBackStatus}
                        title={language === 'es' ? 'Volver al paso anterior (te preguntará antes de cambiar)' : 'Go back one step (will ask before changing)'}>
                        <ChevronDown className="h-4 w-4 mr-1 rotate-90" />{language === 'es' ? 'Anterior' : 'Back'}
                      </Button>
                      <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700"
                        onClick={async () => { await wizardAdvanceStatus(false); await wizardGenerateNewDraft(); }}
                        title={language === 'es' ? 'Enviar email del borrador y avanzar al siguiente paso' : 'Send draft email and advance to next step'}>
                        <Send className="h-4 w-4 mr-1" />{language === 'es' ? 'Enviar y Avanzar' : 'Send & Advance'}
                      </Button>
                      <Button size="sm" variant="outline"
                        onClick={async () => { await wizardAdvanceStatus(true); await wizardGenerateNewDraft(); }}
                        title={language === 'es' ? 'Avanzar sin enviar el email' : 'Advance without sending email'}>
                        <ArrowRight className="h-4 w-4 mr-1" />{language === 'es' ? 'Omitir' : 'Skip'}
                      </Button>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <Button size="sm" variant="outline" onClick={wizardGenerateNewDraft}>
                        <FileText className="h-4 w-4 mr-1" />{language === 'es' ? 'Nuevo Borrador' : 'New Draft'}
                      </Button>
                      {/* Translate */}
                      <LanguageSelector
                        loading={translatingDraft}
                        onSelect={(code) => translateDraft(code)}
                        label={language === 'es' ? 'Traducir' : 'Translate'}
                      />
                      {/* Change to any status */}
                      <div className="relative group">
                        <Button size="sm" variant="outline" className="text-xs">
                          {language === 'es' ? 'Cambiar Estado' : 'Change Status'} <ChevronDown className="h-3 w-3 ml-1" />
                        </Button>
                        <div className="absolute left-0 bottom-full mb-1 bg-white border rounded-md shadow-lg hidden group-hover:block z-20 min-w-[160px] py-1">
                          {['Contactando', 'Consultando', 'Redactor', 'Revision', 'Medio', 'Publicada', 'Incidencia', 'Cancelada'].map(s => (
                            <button key={s} className={`block w-full text-left px-3 py-1.5 text-sm ${s === (wizard.currentStatus || 'Contactando') ? 'font-bold bg-indigo-50 text-indigo-400 cursor-not-allowed' : 'hover:bg-slate-100 cursor-pointer'}`}
                              onClick={() => { if (s !== (wizard.currentStatus || 'Contactando')) wizardChangeStatus(s); }}>
                              <span className={`inline-block w-2 h-2 rounded-full mr-2 ${STATUS_CONFIG[s]?.color || 'bg-slate-300'}`}></span>
                              {STATUS_CONFIG[s]?.label || s}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-between pt-2 border-t">
                    <Button variant="ghost" onClick={() => { openOrder(wizard.orderId); wizardFinish(); }}>
                      <Eye className="h-4 w-4 mr-1" />{language === 'es' ? 'Ver Pedido' : 'View Order'}
                    </Button>
                    <Button className="bg-green-600 hover:bg-green-700" onClick={wizardFinish}>
                      <CheckCircle2 className="h-4 w-4 mr-1" />{language === 'es' ? 'Listo' : 'Done'}
                    </Button>
                  </div>
                </div>
              )}

              {/* Step: Draft error */}
              {wizard.step === 'draft_error' && (
                <div className="space-y-4">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="h-5 w-5 text-green-500" />
                    <h3 className="font-bold text-lg">{language === 'es' ? `Pedido #${wizard.orderNumber || wizard.orderId?.slice(0,8)} creado` : `Order #${wizard.orderNumber || wizard.orderId?.slice(0,8)} created`}</h3>
                  </div>
                  <p className="text-sm text-slate-500">{language === 'es' ? 'El pedido se creó pero no pude generar el borrador.' : 'Order created but I couldn\'t generate the draft.'}</p>

                  <div className="flex items-center gap-2 flex-wrap border-t pt-3">
                    <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700" onClick={async () => { await wizardAdvanceStatus(); }}>
                      <ArrowRight className="h-4 w-4 mr-1" />{language === 'es' ? 'Avanzar Paso' : 'Advance'}
                    </Button>
                    <Button size="sm" variant="outline" onClick={wizardGenerateNewDraft}>
                      <FileText className="h-4 w-4 mr-1" />{language === 'es' ? 'Reintentar Borrador' : 'Retry Draft'}
                    </Button>
                  </div>

                  <div className="flex justify-between pt-2 border-t">
                    <Button variant="ghost" onClick={() => { openOrder(wizard.orderId); wizardFinish(); }}>
                      <Eye className="h-4 w-4 mr-1" />{language === 'es' ? 'Ver Pedido' : 'View Order'}
                    </Button>
                    <Button className="bg-green-600 hover:bg-green-700" onClick={wizardFinish}>
                      <CheckCircle2 className="h-4 w-4 mr-1" />{language === 'es' ? 'Listo' : 'Done'}
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default SupervisorTab;
